/**
 * DEV-1888 («Воронка v2»). Подписи статусов проверки документа сделки.
 *
 * Отдельным модулем, как clientLeadSource/showcaseGallery: строки нужны и слотам карточки сделки,
 * и модалке блокировки перехода, а общий ru.ts правят несколько эпиков сразу.
 * Ключи совпадают с labelKey из `features/deals/utils/documentVerification.ts`.
 */

const ruStatus = {
  none: 'Не загружен',
  pending: 'В очереди на проверку',
  checking: 'Проверяется…',
  verified: 'Подтверждён ИИ',
  rejected: 'Отклонён ИИ',
  failed: 'Ошибка проверки',
  manualVerified: 'Подтверждён вручную',
  manualRejected: 'Отклонён вручную',
}

const enStatus = {
  none: 'Not uploaded',
  pending: 'Queued for check',
  checking: 'Checking…',
  verified: 'Approved by AI',
  rejected: 'Rejected by AI',
  failed: 'Check failed',
  manualVerified: 'Approved manually',
  manualRejected: 'Rejected manually',
}

const vnStatus = {
  none: 'Chưa tải lên',
  pending: 'Đang chờ kiểm tra',
  checking: 'Đang kiểm tra…',
  verified: 'AI đã xác nhận',
  rejected: 'AI đã từ chối',
  failed: 'Kiểm tra thất bại',
  manualVerified: 'Đã xác nhận thủ công',
  manualRejected: 'Đã từ chối thủ công',
}

// Конструктор воронки: правила этапа и обязательные документы (Ф1).
const ruConstructor = {
  tabStages: 'Этапы',
  tabDocumentTypes: 'Типы документов',
  systemTypeHint: 'Системный этап — тип менять нельзя, иначе воронка останется без финала',
  skipTaskCheck: 'Игнорировать незавершённые задачи при переходе на этот этап',
  skipTaskCheckHint:
    'Открытые задачи покинутого этапа снимаются, переход не блокируется. Для этапов «Провал» включено по умолчанию',
  requiredDocuments: 'Обязательные документы',
  tasksSection: 'Задачи этапа',
  addDocument: 'Добавить документ',
  docsCount: '{n} документ | {n} документа | {n} документов',
  pickerTitle: 'Обязательные документы этапа «{name}»',
  pickerEmpty: 'Типов документов пока нет — сначала опишите документ, потом требуйте его на этапе',
  goToTypes: 'К типам документов',
}

const enConstructor = {
  tabStages: 'Stages',
  tabDocumentTypes: 'Document types',
  systemTypeHint: 'System stage — its type is fixed, otherwise the funnel loses its final stage',
  skipTaskCheck: 'Ignore unfinished tasks when moving to this stage',
  skipTaskCheckHint:
    'Open tasks of the stage being left are dropped and the move is not blocked. On “Lost” stages this is on by default',
  requiredDocuments: 'Required documents',
  tasksSection: 'Stage tasks',
  addDocument: 'Add document',
  docsCount: '{n} document | {n} documents',
  pickerTitle: 'Documents required by “{name}”',
  pickerEmpty: 'No document types yet — describe a document first, then require it on a stage',
  goToTypes: 'Go to document types',
}

const vnConstructor = {
  tabStages: 'Giai đoạn',
  tabDocumentTypes: 'Loại tài liệu',
  systemTypeHint: 'Giai đoạn hệ thống — không đổi được loại, nếu không phễu sẽ mất giai đoạn cuối',
  skipTaskCheck: 'Bỏ qua công việc chưa xong khi chuyển sang giai đoạn này',
  skipTaskCheckHint:
    'Công việc đang mở của giai đoạn rời đi sẽ được gỡ, việc chuyển không bị chặn. Với giai đoạn «Thất bại» mặc định đang bật',
  requiredDocuments: 'Tài liệu bắt buộc',
  tasksSection: 'Công việc của giai đoạn',
  addDocument: 'Thêm tài liệu',
  docsCount: '{n} tài liệu',
  pickerTitle: 'Tài liệu bắt buộc của giai đoạn «{name}»',
  pickerEmpty: 'Chưa có loại tài liệu nào — hãy mô tả tài liệu trước, rồi mới yêu cầu ở giai đoạn',
  goToTypes: 'Tới danh mục loại tài liệu',
}

// Справочник типов документов (Ф2).
const ruDocumentTypes = {
  title: 'Типы документов',
  subtitle:
    'Типы документов, которые ИИ проверяет при загрузке к сделке: описание, форматы, порог уверенности и схема полей',
  add: 'Добавить тип документа',
  editorTitle: 'Тип документа',
  name: 'Название',
  nameHint: 'Переведём на остальные языки автоматически',
  nameRequired: 'Без названия тип не сохранить',
  prompt: 'Описание для ИИ — как выглядит документ и что должно быть читаемо',
  promptPlaceholder:
    'Разворот паспорта с фотографией: ФИО, серия и номер, дата выдачи, орган выдачи. Все поля читаемы, без бликов, разворот не обрезан.',
  formats: 'Допустимые форматы',
  columnFormats: 'Форматы',
  columnThreshold: 'Порог',
  columnFields: 'Полей',
  formatsRequired: 'Оставьте хотя бы один формат — иначе загрузить документ будет нечем',
  threshold: 'Порог уверенности',
  thresholdHint: '% · ниже порога документ отклоняется, менеджер видит причины',
  fields: 'Извлекаемые поля',
  fieldsHint: 'ключ · подпись · тип · обязательное',
  addField: 'Добавить поле',
  fieldKey: 'ключ',
  fieldLabel: 'подпись',
  fieldKeyInvalid: 'Латиница в нижнем регистре, цифры и _, до 40 символов',
  fieldKeyDuplicate: 'Такой ключ уже есть',
  fieldLabelRequired: 'Нужна подпись поля',
  type: { string: 'Строка', number: 'Число', date: 'Дата', money: 'Деньги' },
  usedIn: 'Используется на этапах',
  notUsed: 'Не используется',
  empty: 'Типов документов пока нет',
  emptyHint: 'Опишите документы — ИИ будет проверять их автоматически при загрузке к сделке',
  save: 'Сохранить тип',
  saved: 'Тип документа сохранён',
  deleted: 'Тип документа удалён',
  delete: {
    title: 'Удалить тип «{name}»?',
    note: 'Будет убран из требований этапов: {stages}. Загруженные файлы останутся в сделках вместе с результатом проверки.',
    noteUnused:
      'Тип не требуется ни на одном этапе. Загруженные файлы останутся в сделках вместе с результатом проверки.',
    confirm: 'Удалить тип',
  },
}

const enDocumentTypes = {
  title: 'Document types',
  subtitle:
    'Documents the AI checks when they are attached to a deal: description, formats, confidence threshold and field schema',
  add: 'Add document type',
  editorTitle: 'Document type',
  name: 'Name',
  nameHint: 'Other languages are translated automatically',
  nameRequired: 'A type without a name cannot be saved',
  prompt: 'Description for the AI — what the document looks like and what must be readable',
  promptPlaceholder:
    'Passport page with a photo: full name, series and number, issue date, issuing authority. All fields readable, no glare, page not cropped.',
  formats: 'Allowed formats',
  columnFormats: 'Formats',
  columnThreshold: 'Threshold',
  columnFields: 'Fields',
  formatsRequired: 'Keep at least one format, otherwise nothing can be uploaded',
  threshold: 'Confidence threshold',
  thresholdHint: '% · below the threshold the document is rejected and the manager sees why',
  fields: 'Extracted fields',
  fieldsHint: 'key · label · type · required',
  addField: 'Add field',
  fieldKey: 'key',
  fieldLabel: 'label',
  fieldKeyInvalid: 'Lowercase letters, digits and _, up to 40 characters',
  fieldKeyDuplicate: 'This key is already used',
  fieldLabelRequired: 'The field needs a label',
  type: { string: 'Text', number: 'Number', date: 'Date', money: 'Money' },
  usedIn: 'Required by stages',
  notUsed: 'Not used',
  empty: 'No document types yet',
  emptyHint: 'Describe your documents — the AI will check them on upload',
  save: 'Save type',
  saved: 'Document type saved',
  deleted: 'Document type deleted',
  delete: {
    title: 'Delete type “{name}”?',
    note: 'It will be removed from the requirements of: {stages}. Uploaded files stay in their deals together with the check result.',
    noteUnused:
      'No stage requires this type. Uploaded files stay in their deals together with the check result.',
    confirm: 'Delete type',
  },
}

const vnDocumentTypes = {
  title: 'Loại tài liệu',
  subtitle:
    'Các loại tài liệu AI kiểm tra khi đính kèm vào giao dịch: mô tả, định dạng, ngưỡng tin cậy và sơ đồ trường',
  add: 'Thêm loại tài liệu',
  editorTitle: 'Loại tài liệu',
  name: 'Tên',
  nameHint: 'Các ngôn ngữ còn lại sẽ được dịch tự động',
  nameRequired: 'Không có tên thì không lưu được',
  prompt: 'Mô tả cho AI — tài liệu trông thế nào và phần nào phải đọc được',
  promptPlaceholder:
    'Trang hộ chiếu có ảnh: họ tên, số hiệu, ngày cấp, nơi cấp. Mọi trường đều đọc được, không loá, không bị cắt.',
  formats: 'Định dạng cho phép',
  columnFormats: 'Định dạng',
  columnThreshold: 'Ngưỡng',
  columnFields: 'Số trường',
  formatsRequired: 'Hãy giữ ít nhất một định dạng, nếu không sẽ không tải lên được gì',
  threshold: 'Ngưỡng tin cậy',
  thresholdHint: '% · dưới ngưỡng tài liệu bị từ chối, quản lý thấy lý do',
  fields: 'Trường trích xuất',
  fieldsHint: 'khoá · nhãn · kiểu · bắt buộc',
  addField: 'Thêm trường',
  fieldKey: 'khoá',
  fieldLabel: 'nhãn',
  fieldKeyInvalid: 'Chữ thường, chữ số và _, tối đa 40 ký tự',
  fieldKeyDuplicate: 'Khoá này đã có',
  fieldLabelRequired: 'Trường cần có nhãn',
  type: { string: 'Chuỗi', number: 'Số', date: 'Ngày', money: 'Tiền' },
  usedIn: 'Được yêu cầu ở giai đoạn',
  notUsed: 'Chưa dùng',
  empty: 'Chưa có loại tài liệu nào',
  emptyHint: 'Hãy mô tả tài liệu — AI sẽ tự kiểm tra khi tải lên',
  save: 'Lưu loại tài liệu',
  saved: 'Đã lưu loại tài liệu',
  deleted: 'Đã xoá loại tài liệu',
  delete: {
    title: 'Xoá loại «{name}»?',
    note: 'Sẽ bị gỡ khỏi yêu cầu của giai đoạn: {stages}. Tệp đã tải lên vẫn nằm trong giao dịch cùng kết quả kiểm tra.',
    noteUnused:
      'Không giai đoạn nào yêu cầu loại này. Tệp đã tải lên vẫn nằm trong giao dịch cùng kết quả kiểm tra.',
    confirm: 'Xoá loại tài liệu',
  },
}

// Модалка «переход заблокирован» — документы рядом с задачами (Ф4).
const ruBlocked = {
  headingDocuments: 'Сначала загрузите документы',
  headingBoth: 'Сначала выполните задачу и загрузите документ',
  bodyDocuments: 'Чтобы перевести сделку на этап «{stage}», нужно подтвердить документы:',
  bodyBoth:
    'Чтобы перевести сделку на этап «{stage}», нужно закрыть обязательную задачу текущего этапа и подтвердить документы:',
  openDocuments: 'К документам',
  docReason: {
    notUploaded: 'Не загружен',
    checking: 'Проверяется — дождитесь результата',
    rejected: 'Отклонён — нужен другой файл или подтверждение руководителя',
    failed: 'Проверка не прошла — отправьте документ заново',
  },
}

const enBlocked = {
  headingDocuments: 'Upload the documents first',
  headingBoth: 'Finish the task and upload the document first',
  bodyDocuments: 'To move the deal to “{stage}”, these documents must be approved:',
  bodyBoth:
    'To move the deal to “{stage}”, close the mandatory task of the current stage and get these documents approved:',
  openDocuments: 'Go to documents',
  docReason: {
    notUploaded: 'Not uploaded',
    checking: 'Being checked — wait for the result',
    rejected: 'Rejected — upload another file or ask a supervisor to approve it',
    failed: 'The check failed — send the document again',
  },
}

const vnBlocked = {
  headingDocuments: 'Hãy tải tài liệu lên trước',
  headingBoth: 'Hãy hoàn thành công việc và tải tài liệu lên trước',
  bodyDocuments: 'Để chuyển giao dịch sang «{stage}», cần xác nhận các tài liệu:',
  bodyBoth:
    'Để chuyển giao dịch sang «{stage}», cần đóng công việc bắt buộc của giai đoạn hiện tại và xác nhận các tài liệu:',
  openDocuments: 'Tới tài liệu',
  docReason: {
    notUploaded: 'Chưa tải lên',
    checking: 'Đang kiểm tra — hãy chờ kết quả',
    rejected: 'Bị từ chối — cần tệp khác hoặc quản lý xác nhận',
    failed: 'Kiểm tra thất bại — hãy gửi lại tài liệu',
  },
}

// Слоты документов в карточке сделки (Ф3).
const ruDocuments = {
  title: 'Документы сделки',
  forNext: 'Для перехода на этап «{stage}»',
  forCurrent: 'Текущий этап «{stage}»',
  forPast:
    'Загруженные ранее · {n} документ | Загруженные ранее · {n} документа | Загруженные ранее · {n} документов',
  progress: '{done} / {total} подтверждено',
  noRequirements: 'На этом этапе документы не требуются',
  unknownType: 'Тип документа удалён',
  uploadHint: '{formats} · до {size}',
  upload: 'Загрузить',
  uploadAnother: 'Загрузить другой',
  uploading: 'Загружаем…',
  open: 'Открыть',
  fields: 'Распознанные поля ({n})',
  recheck: 'Перепроверить',
  approve: 'Подтвердить вручную',
  reject: 'Отклонить',
  looksLike: 'Похоже на «{type}»',
  tooLarge: 'Файл больше 15 МБ — ИИ такой не проверит',
  recheckError: 'Не удалось отправить документ на перепроверку',
  verifyError: 'Не удалось сохранить решение по документу',
  review: {
    approveTitle: 'Подтвердить документ вручную',
    rejectTitle: 'Отклонить документ',
    comment: 'Комментарий',
    commentPlaceholder: 'Например: скан старого образца, данные сверены с оригиналом',
    commentRequired: 'Напишите, что не так — менеджеру нужно знать, что переснимать',
  },
}

const enDocuments = {
  title: 'Deal documents',
  forNext: 'Required to move to “{stage}”',
  forCurrent: 'Current stage “{stage}”',
  forPast: 'Uploaded earlier · {n} document | Uploaded earlier · {n} documents',
  progress: '{done} / {total} approved',
  noRequirements: 'This stage requires no documents',
  unknownType: 'Document type deleted',
  uploadHint: '{formats} · up to {size}',
  upload: 'Upload',
  uploadAnother: 'Upload another',
  uploading: 'Uploading…',
  open: 'Open',
  fields: 'Extracted fields ({n})',
  recheck: 'Check again',
  approve: 'Approve manually',
  reject: 'Reject',
  looksLike: 'Looks like “{type}”',
  tooLarge: 'The file is over 15 MB — the AI cannot check it',
  recheckError: 'Could not send the document for another check',
  verifyError: 'Could not save the decision on this document',
  review: {
    approveTitle: 'Approve the document manually',
    rejectTitle: 'Reject the document',
    comment: 'Comment',
    commentPlaceholder: 'For example: an older scan format, data verified against the original',
    commentRequired: 'Say what is wrong — the manager needs to know what to re-shoot',
  },
}

const vnDocuments = {
  title: 'Tài liệu của giao dịch',
  forNext: 'Cần để chuyển sang «{stage}»',
  forCurrent: 'Giai đoạn hiện tại «{stage}»',
  forPast: 'Đã tải trước đó · {n} tài liệu',
  progress: 'đã xác nhận {done} / {total}',
  noRequirements: 'Giai đoạn này không yêu cầu tài liệu',
  unknownType: 'Loại tài liệu đã bị xoá',
  uploadHint: '{formats} · tối đa {size}',
  upload: 'Tải lên',
  uploadAnother: 'Tải tệp khác',
  uploading: 'Đang tải lên…',
  open: 'Mở',
  fields: 'Trường đã nhận dạng ({n})',
  recheck: 'Kiểm tra lại',
  approve: 'Xác nhận thủ công',
  reject: 'Từ chối',
  looksLike: 'Trông giống «{type}»',
  tooLarge: 'Tệp lớn hơn 15 MB — AI không kiểm tra được',
  recheckError: 'Không gửi được tài liệu để kiểm tra lại',
  verifyError: 'Không lưu được quyết định về tài liệu',
  review: {
    approveTitle: 'Xác nhận tài liệu thủ công',
    rejectTitle: 'Từ chối tài liệu',
    comment: 'Bình luận',
    commentPlaceholder: 'Ví dụ: bản scan mẫu cũ, dữ liệu đã đối chiếu với bản gốc',
    commentRequired: 'Hãy ghi rõ vấn đề — quản lý cần biết phải chụp lại gì',
  },
}

// Баннер блокировки перехода в карточке задач: строка про документы.
const ruBlock = {
  uploadDocuments: 'загрузите {n} документ | загрузите {n} документа | загрузите {n} документов',
  blockedByDocuments: 'Загрузите документы этапа и дождитесь подтверждения',
  documentsLine: 'Документы: {done} / {total} подтверждено',
}
const enBlock = {
  uploadDocuments: 'upload {n} document | upload {n} documents',
  blockedByDocuments: 'Upload the stage documents and wait for approval',
  documentsLine: 'Documents: {done} / {total} approved',
}
const vnBlock = {
  uploadDocuments: 'hãy tải lên {n} tài liệu',
  blockedByDocuments: 'Hãy tải tài liệu của giai đoạn và chờ xác nhận',
  documentsLine: 'Tài liệu: đã xác nhận {done} / {total}',
}

export const documentTypesMessages = {
  ru: {
    deals: {
      detail: {
        documents: { ...ruDocuments, status: ruStatus },
        block: ruBlock,
      },
    },
    pipeline: { constructor: ruConstructor, documentTypes: ruDocumentTypes, blocked: ruBlocked },
  },
  en: {
    deals: {
      detail: {
        documents: { ...enDocuments, status: enStatus },
        block: enBlock,
      },
    },
    pipeline: { constructor: enConstructor, documentTypes: enDocumentTypes, blocked: enBlocked },
  },
  vn: {
    deals: {
      detail: {
        documents: { ...vnDocuments, status: vnStatus },
        block: vnBlock,
      },
    },
    pipeline: { constructor: vnConstructor, documentTypes: vnDocumentTypes, blocked: vnBlocked },
  },
}
